import React, { useEffect, useRef, useState } from 'react';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';

interface PhoneInputProps {
    value?: string;
    onChange?: (phoneNumber: string, isValid: boolean, countryData: any) => void;
    placeholder?: string;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    id?: string;
    name?: string;
}

export function PhoneInput({
    value = '',
    onChange,
    placeholder = 'Phone number (optional)',
    className = '',
    required = false,
    disabled = false,
    id = 'phone',
    name = 'phone',
}: PhoneInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const itiRef = useRef<any>(null);
    const emitChangeRef = useRef<(forceTouched?: boolean) => void>(() => undefined);
    const countryChangeHandlerRef = useRef<(() => void) | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        const input = inputRef.current;
        if (input && !itiRef.current) {
            try {
                itiRef.current = intlTelInput(input, {
                    initialCountry: 'us',
                    utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@19.2.16/build/js/utils.js',
                    separateDialCode: true,
                    nationalMode: false,
                    formatOnDisplay: true,
                    autoPlaceholder: 'aggressive',
                } as any);

                countryChangeHandlerRef.current = () => emitChangeRef.current();
                input.addEventListener('countrychange', countryChangeHandlerRef.current);
            } catch (initError) {
                console.log('[PhoneInput] Initialization error:', initError);
            }
        }

        return () => {
            if (input && countryChangeHandlerRef.current) {
                input.removeEventListener('countrychange', countryChangeHandlerRef.current);
                countryChangeHandlerRef.current = null;
            }
            if (itiRef.current) {
                try {
                    itiRef.current.destroy();
                } catch (destroyError) {
                    console.log('[PhoneInput] Destroy error:', destroyError);
                }
                itiRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (itiRef.current && value !== itiRef.current.getNumber()) {
            try {
                itiRef.current.setNumber(value);
            } catch (err) {
                // Ignore errors when setting number
            }
        }
    }, [value]);

    const emitChange = (forceTouched = false) => {
        const input = inputRef.current;
        const rawValue = input?.value || '';
        const digits = rawValue.replace(/\D/g, '');
        const shouldShowError = touched || forceTouched;

        if (itiRef.current) {
            try {
                const number = itiRef.current.getNumber();
                const countryData = itiRef.current.getSelectedCountryData();
                const isValid = !rawValue.trim() ? !required : itiRef.current.isValidNumber();
                if (shouldShowError && rawValue.trim() && !isValid) {
                    setError('Enter a valid phone number');
                } else {
                    setError(null);
                }
                onChange?.(number || rawValue, isValid, countryData);
                return;
            } catch {
                // Fall through to a minimal E.164-style fallback below.
            }
        }

        const fallback = digits ? `+${digits}` : '';
        const isValid = !rawValue.trim() ? !required : digits.length >= 7;
        if (shouldShowError && rawValue.trim() && !isValid) {
            setError('Enter a valid phone number');
        } else {
            setError(null);
        }
        onChange?.(fallback, isValid, null);
    };
    emitChangeRef.current = emitChange;

    const handleInput = () => {
        emitChange();
    };

    const handleBlur = () => {
        setTouched(true);

        if (inputRef.current) {
            emitChange(true);
        }
    };

    return (
        <div className="phone-input-container">
            <input
                ref={inputRef}
                type="tel"
                id={id}
                name={name}
                placeholder={placeholder}
                className={`w-full px-4 py-3 bg-sl-bg border ${error ? 'border-red-500' : 'border-sl-surface-2'} rounded-lg focus:ring-2 focus:ring-sl-accent focus:border-transparent text-white placeholder:text-slate-500 ${className}`}
                onInput={handleInput}
                onBlur={handleBlur}
                required={required}
                disabled={disabled}
                autoComplete="tel"
            />
            {error && (
                <p className="text-red-400 text-xs mt-1">{error}</p>
            )}
            <style>{`
        .iti { width: 100%; }
        .iti__flag-container { z-index: 10; }
        .iti__country-list { 
          background-color: #1e293b; 
          color: white;
          border: 1px solid #334155;
          max-height: 200px;
          z-index: 100;
        }
        .iti__country:hover { background-color: #334155; }
        .iti__country.iti__highlight { background-color: #334155; }
        .iti__dial-code { color: #94a3b8; }
        .iti__selected-flag { 
          background-color: transparent !important; 
          border-right: 1px solid #334155;
        }
        .iti__selected-flag:hover { background-color: transparent !important; }
        .iti--allow-dropdown .iti__flag-container:hover .iti__selected-flag {
          background-color: rgba(255,255,255,0.05) !important;
        }
        .iti__arrow { border-top-color: #94a3b8; }
        .iti__arrow--up { border-bottom-color: #94a3b8; }
        .iti__search-input {
          background-color: #0f172a;
          color: white;
          border-color: #334155;
        }
        .iti__search-input::placeholder { color: #64748b; }
      `}</style>
        </div>
    );
}

export default PhoneInput;
